import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';
import { rem } from '../../utils/utils';
import tokensToTheme from '../../tokens/tokensToTheme.json';
import { TagProps } from './Tag';

const { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers([
  'container',
  'icon',
  'label',
  'closeButton',
  'skeleton',
]);

const sizes = {
  md: definePartsStyle((props) => {
    const { iconName, onClose, withIcon } = props as TagProps;
    const leftIcon = iconName || withIcon;

    return {
      closeButton: {
        height: '24',
        marginLeft: '8',
        width: '24',
      },
      container: {
        paddingLeft: leftIcon ? rem(6) : rem(12),
        paddingRight: onClose ? rem(4) : rem(12),
        paddingY: leftIcon || onClose ? rem(4) : rem(6),
      },
      label: tokensToTheme.textStyles['comp/tag/md'],
    };
  }),
  sm: definePartsStyle((props) => {
    const { iconName, onClose, withIcon } = props as TagProps;
    const leftIcon = iconName || withIcon;

    return {
      closeButton: {
        height: '20',
        width: '20',
      },
      container: {
        paddingLeft: leftIcon ? rem(4) : rem(8),
        paddingRight: onClose ? rem(2) : rem(8),
        paddingY: onClose ? rem(2) : rem(4),
      },
      label: tokensToTheme.textStyles['comp/tag/sm'],
    };
  }),
};

type TagColors = NonNullable<TagProps['colorScheme']>;
type MappedColor = {
  color: 'interactive' | 'warning' | 'critical' | 'success' | 'info' | 'neutral';
  isFilled: boolean;
};

const schemeMap: Record<TagColors, MappedColor> = {
  blue: { color: 'info', isFilled: false },
  green: { color: 'success', isFilled: false },
  neutral: { color: 'neutral', isFilled: false },
  purple: { color: 'interactive', isFilled: false },
  purpleFilled: { color: 'interactive', isFilled: true },
  red: { color: 'critical', isFilled: false },
  yellow: { color: 'warning', isFilled: false },
};

const baseStyle = definePartsStyle((props) => {
  const { colorScheme = 'neutral' } = props;
  const { color, isFilled } = schemeMap[colorScheme as TagColors];
  const normalTextColor = color === 'neutral' ? 'text.primary' : `sys.${color}.strong`;
  const scheme = {
    backgroundColor: `sys.${color}.${isFilled ? 'bold' : 'subtle'}`,
    borderColor: `sys.${color}.${isFilled ? 'bold' : 'muted'}`,
    color: isFilled ? 'text.on-color' : normalTextColor,
    fontWeight: isFilled ? 'semibold' : 'normal',
  };

  return {
    closeButton: {
      _disabled: {
        pointerEvents: 'none',
      },
      _focus: {
        boxShadow: '0 0 0 1px var(--colors-purple-70) inset',
      },
      _hover: {
        backgroundColor: `sys.${color}.${isFilled ? 'highlight' : 'moderate'}`,
      },
      borderRadius: rem(2),
      marginInlineStart: '4',
    },
    container: {
      border: '1px solid',
      borderRadius: '4',
      ...scheme,
      _disabled: {
        backgroundColor: `sys.neutral.${isFilled ? 'muted' : 'minimal'}`,
        borderColor: `sys.neutral.${isFilled ? 'muted' : 'moderate'}`,
        color: isFilled ? 'text.on-color' : 'text.disabled',
        cursor: 'not-allowed',
      },
      _loading: {
        backgroundColor: `sys.neutral.${isFilled ? 'muted' : 'subtle'}`,
        borderColor: `sys.neutral.${isFilled ? 'muted' : 'subtle'}`,
        color: isFilled ? 'text.on-color' : 'text.disabled',
        cursor: 'wait',
      },
    },
    icon: {
      marginInlineEnd: '4',
    },
    label: {
      display: 'inline',
    },
    skeleton: {
      backgroundColor: 'sys.neutral.muted',
      height: '8',
      minWidth: '32',
    },
  };
});

const TagTheme = defineMultiStyleConfig({
  baseStyle,
  sizes,
});

export default TagTheme;
